/**
 * components/client-detail/ClientTabBar.tsx
 *
 * Browser-tab style navigation for the client detail page.
 * Active tab: white background, bold text, blue accent bar on top edge,
 * subtle upward shadow. Inactive tabs: mid-grey background, muted text.
 * The visual contrast is deliberately strong so the active state is
 * immediately legible by all users.
 *
 * Tabs are flush (no gap), with shared borders, creating a proper
 * browser-tab metaphor that opens into the white content card below.
 *
 * Accessibility: role="tablist" / role="tab" / aria-selected.
 *
 * Created Apr 2026.
 * Updated Apr 2026 — stronger inactive/active contrast, blue accent bar,
 *   removed inter-tab gap.
 */

import { AlignLeft, FolderOpen, Clock } from 'lucide-react';
import type { ActiveTab } from '../../pages/ClientDetail';

const TABS: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
  { id: 'informations', label: 'Informations', icon: <AlignLeft className="w-3.5 h-3.5" /> },
  { id: 'documents', label: 'Documents', icon: <FolderOpen className="w-3.5 h-3.5" /> },
  { id: 'historique', label: 'Historique', icon: <Clock className="w-3.5 h-3.5" /> },
];

interface ClientTabBarProps {
  active: ActiveTab;
  onChange: (tab: ActiveTab) => void;
}

function ClientTabBar({ active, onChange }: ClientTabBarProps) {
  return (
    <div
      role="tablist"
      aria-label="Vue du dossier client"
      className="flex items-end"
    >
      {TABS.map(({ id, label, icon }, idx) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(id)}
            className={`
              relative flex items-center gap-2 px-5 py-3 text-sm
              rounded-t-xl
              border border-b-0
              transition-colors duration-150
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-inset
              ${idx > 0 ? '-ml-px' : ''}
              ${isActive
                ? 'bg-white text-neutral-900 font-semibold border-neutral-200 z-10 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]'
                : 'bg-neutral-200 text-neutral-500 font-medium border-neutral-300 hover:bg-neutral-100 hover:text-neutral-700 z-0'
              }
            `}
          >
            <span className={isActive ? 'text-primary-600' : 'text-neutral-400'}>
              {icon}
            </span>
            {label}

            {/* Cover bottom border to fuse with content card */}
            {isActive && (
              <span aria-hidden className="absolute -bottom-px left-0 right-0 h-px bg-white" />
            )}
          </button>
        );
      })}
    </div>
  );
}


export default ClientTabBar